import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { REBOOT_VAN_X, REBOOT_VAN_Z } from './WorldBuilder.js';
import { createToonGradientMap, toonMat } from './materials.js';
import { randomElimName, randomBotRadioQuip } from './FpsBotNames.js';
import type { FpsLoot } from './FpsLoot.js';
import type { WeaponKind } from './WeaponSystem.js';
import { type WeaponRarity, RARITY_LABELS } from './FpsWeaponRarity.js';
import { FpsBotBuilds } from './FpsBotBuilds.js';
import type { FpsBuildPlacer } from './FpsBuildPlacer.js';
import { FpsCharacterAnimator, type CharAnimState } from './FpsCharacterAnimator.js';
import { getBotEmoteDuration, type BotEmoteKind } from './FpsEmoteWheel.js';

const BOT_PRESETS = [
  { hue: 0.0,  headScaleX: 1.0,  headScaleY: 1.0,  accessory: 'none'     as const },
  { hue: 0.1,  headScaleX: 1.1,  headScaleY: 0.95, accessory: 'cap'      as const },
  { hue: 0.25, headScaleX: 0.9,  headScaleY: 1.05, accessory: 'backpack' as const },
  { hue: 0.4,  headScaleX: 1.0,  headScaleY: 1.1,  accessory: 'cap'      as const },
  { hue: 0.55, headScaleX: 1.05, headScaleY: 0.9,  accessory: 'none'     as const },
  { hue: 0.65, headScaleX: 0.95, headScaleY: 1.0,  accessory: 'backpack' as const },
  { hue: 0.75, headScaleX: 1.1,  headScaleY: 1.0,  accessory: 'none'     as const },
  { hue: 0.85, headScaleX: 1.0,  headScaleY: 0.95, accessory: 'cap'      as const },
] as const;

const CHATTER_MIN_INTERVAL = 12;
const CHATTER_MAX_INTERVAL = 25;
const CHATTER_COOLDOWN = 8;
const MAX_BOTS = 10;
const REINFORCEMENT_WAVE_SIZE = 3;
const REINFORCEMENT_MAX_TOTAL = 14;
const SQUAD_SIZE = 2;

/** Accent ring / minimap colors for bot squads 0–4 (pairs of two). */
export const BOT_SQUAD_COLORS = ['#ff6644', '#44aaff', '#ffdd44', '#cc66ff', '#44ffaa'] as const;

export function getBotSquadColor(squadId: number): string {
  return BOT_SQUAD_COLORS[Math.max(0, Math.min(BOT_SQUAD_COLORS.length - 1, squadId))]!;
}

const SQUADMATE_CRAWL_CHANCE = 0.2;
const MAX_HP = 100;
const SIGHT_RANGE = 26;
const SHOOT_RANGE = 22;
const CHASE_SPEED = 3.6;
const PATROL_SPEED = 1.7;
const SQUADMATE_CRAWL_SPEED = PATROL_SPEED * 0.38;
const RAGDOLL_LIFE = 2.6;
const KNOCK_BLEEDOUT = 8;
const INITIAL_AGGRO_DELAY = 3.5;
const BOT_HIT_CHANCE = 0.18;
const CHEST_LOOT_RANGE = 2.2;
const CHEST_SHIELD_RESTORE = 25;
const MELEE_RANGE = 2.5;
const MELEE_CONE_DOT = 0.72;
const BOT_SHOOT_BUILD_CHANCE = 0.05;
const BOT_COMBAT_BUILD_RANGE = 22;
const REBOOT_VAN_RADIUS = 8;
const REBOOT_VAN_HOLD = 4;
const BOT_RIM_LIGHT_RANGE = 40;
const BOT_NAMEPLATE_FADE_START = 14;
const BOT_NAMEPLATE_FADE_END = 48;

export type BotLoadout = 'ar' | 'shotgun' | 'smg' | 'sniper';
export type BotRole = 'rusher' | 'builder' | 'flanker' | 'sniper' | 'support';

type BotRoleProfile = {
  preferredDistance: number;
  strafeSpeed: number;
  accuracyMult: number;
  fireCdMult: number;
  chaseSpeedMult: number;
};

const BOT_ROLE_ORDER: BotRole[] = ['rusher', 'builder', 'flanker', 'sniper', 'support'];

const BOT_ROLE_PROFILES: Record<BotRole, BotRoleProfile> = {
  rusher: { preferredDistance: 7.5, strafeSpeed: 3.9, accuracyMult: 0.95, fireCdMult: 0.94, chaseSpeedMult: 1.08 },
  builder: { preferredDistance: 13, strafeSpeed: 3.2, accuracyMult: 0.98, fireCdMult: 1.04, chaseSpeedMult: 0.98 },
  flanker: { preferredDistance: 15, strafeSpeed: 4.35, accuracyMult: 0.94, fireCdMult: 1.0, chaseSpeedMult: 1.04 },
  sniper: { preferredDistance: 24, strafeSpeed: 2.45, accuracyMult: 1.08, fireCdMult: 1.12, chaseSpeedMult: 0.94 },
  support: { preferredDistance: 18, strafeSpeed: 2.9, accuracyMult: 1.02, fireCdMult: 1.08, chaseSpeedMult: 0.96 },
};

const SQUAD_FIRE_STAGGER = 0.34;
const BOT_HEAVY_DAMAGE = 30;
const BOT_BUILDER_PRESSURE_BUILD_CHANCE_PER_SEC = 0.42;
const BOT_CALLOUT_CHANCE = 0.35;
const BOT_INTENT_CALLOUT_COOLDOWN = 9;
const BOT_SNIPER_GLINT_WINDOW = 1.05;
const BOT_SNIPER_GLINT_DURATION = 0.72;
const BOT_SNIPER_GLINT_COOLDOWN = 3.25;
const PLAYER_SNIPER_GLINT_RANGE = 52;
const PLAYER_SNIPER_GLINT_REACT_CD = 4.5;
const BOT_PUSH_TELEGRAPH_WINDOW = 0.48;
const BOT_PUSH_TELEGRAPH_COOLDOWN = 3.75;
const BOT_BUILD_TELEGRAPH_WINDOW = 0.38;
const BOT_BUILD_TELEGRAPH_COOLDOWN = 2.25;
const BOT_BUILD_RECOVERY_PAUSE = 0.28;

type BotCalloutKind = 'push' | 'build' | 'flank' | 'retreat' | 'cracked';

export type BotSquadTacticalKind = 'push' | 'retreat' | 'heal';

const BOT_SQUAD_TACTICAL_CALLOUTS: Record<BotSquadTacticalKind, readonly string[]> = {
  push: ['Push with me!', 'Let\'s take this fight!', 'Moving up — cover me!'],
  retreat: ['Fall back!', 'Reset — we\'re cracked!', 'Back out, heal up!'],
  heal: ['Need heals!', 'Popping a med!', 'Healing up — hold cover!'],
};

const BOT_ROLE_CALLOUTS: Record<BotRole, Record<BotCalloutKind, readonly string[]>> = {
  rusher: {
    push: ['Pushing close!', 'Closing gap!'],
    build: ['Cover up!'],
    flank: ['Swinging wide!'],
    retreat: ['Backing out!'],
    cracked: ['Cracked, reset!'],
  },
  builder: {
    push: ['Moving with cover!'],
    build: ['Wall going up!', 'Boxing for cover!'],
    flank: ['Building a side angle!'],
    retreat: ['Resetting behind wall!'],
    cracked: ['Cracked, full box!'],
  },
  flanker: {
    push: ['Taking side angle!'],
    build: ['Cover my rotate!'],
    flank: ['Going wide!', 'I have an angle!'],
    retreat: ['Losing angle, reset!'],
    cracked: ['Cracked, slipping out!'],
  },
  sniper: {
    push: ['Holding long sight!'],
    build: ['Need a peek wall!'],
    flank: ['Repositioning sightline!'],
    retreat: ['Dropping sightline!'],
    cracked: ['Cracked, off scope!'],
  },
  support: {
    push: ['Moving to support!'],
    build: ['Covering the team!'],
    flank: ['Watching side!'],
    retreat: ['Falling back to heal!'],
    cracked: ['Cracked, need cover!'],
  },
};

type BotLoadoutProfile = {
  sightRange: number;
  shootRange: number;
  shootCd: number;
  damage: number;
  spreadMult: number;
  gunColor: number;
  accentColor: number;
  tag: string;
};

const BOT_LOADOUT_PROFILES: Record<BotLoadout, BotLoadoutProfile> = {
  ar: {
    sightRange: SIGHT_RANGE,
    shootRange: SHOOT_RANGE,
    shootCd: 1.35,
    damage: 4.5,
    spreadMult: 1,
    gunColor: 0x3a3a45,
    accentColor: 0xff6644,
    tag: '',
  },
  shotgun: {
    sightRange: 18,
    shootRange: 14,
    shootCd: 1.65,
    damage: 9,
    spreadMult: 2.25,
    gunColor: 0x4a3528,
    accentColor: 0xff8844,
    tag: 'SG',
  },
  smg: {
    sightRange: 24,
    shootRange: 20,
    shootCd: 0.75,
    damage: 2.8,
    spreadMult: 1.35,
    gunColor: 0x2a4a3a,
    accentColor: 0x44ffaa,
    tag: 'SMG',
  },
  sniper: {
    sightRange: 48,
    shootRange: 40,
    shootCd: 2.35,
    damage: 14,
    spreadMult: 0.42,
    gunColor: 0x2a3548,
    accentColor: 0x88ccff,
    tag: 'SR',
  },
};

function pickBotLoadout(): BotLoadout {
  const roll = Math.random();
  if (roll < 0.12) return 'sniper';
  if (roll < 0.42) return 'ar';
  if (roll < 0.68) return 'smg';
  return 'shotgun';
}

export type FpsStormContext = {
  isOutside: (x: number, z: number) => boolean;
  centerX: number;
  centerZ: number;
  safeRadius: number;
};

type CombatTarget = {
  kind: 'player' | 'bot';
  pos: THREE.Vector3;
  dist: number;
  bot?: Bot;
};

function baseBotHitChance(dist: number): number {
  if (dist < 6) return 0.32;
  if (dist < 12) return 0.24;
  return BOT_HIT_CHANCE;
}

export type BotShotEvent = {
  origin: THREE.Vector3;
  dir: THREE.Vector3;
  damage: number;
  botPos: THREE.Vector3;
  targetPos: THREE.Vector3;
  botName: string;
  botId: number;
  loadout: BotLoadout;
};

export type BotFootstepEvent = { x: number; z: number };

export type BotRebootEvent = { name: string };

export type BotUpdateResult = {
  damageToPlayer: number;
  tracerEvents: BotShotEvent[];
  footstepEvents: BotFootstepEvent[];
  buildCoverEvents: string[];
  rebootEvents: BotRebootEvent[];
};

const BOT_STEP_DIST = 1.72;

export type BotHitResult = {
  killed: boolean;
  knocked?: boolean;
  damage: number;
  name: string;
  headshot: boolean;
  shieldHit?: boolean;
  pos: THREE.Vector3;
};

type BotState = 'patrol' | 'chase' | 'shoot' | 'knocked' | 'dead';

interface Bot {
  id: number;
  root: THREE.Group;
  body: THREE.Group;
  head: THREE.Mesh;
  torso: THREE.Mesh;
  leftArm: THREE.Mesh;
  rightArm: THREE.Mesh;
  leftLeg: THREE.Mesh;
  rightLeg: THREE.Mesh;
  gun: THREE.Group;
  hpBar: HTMLDivElement;
  hpFill: HTMLDivElement;
  nameTag: HTMLDivElement;
  hp: number;
  shield: number;
  state: BotState;
  patrolTarget: THREE.Vector3;
  patrolWait: number;
  shootCd: number;
  walkPhase: number;
  velocity: THREE.Vector3;
  deathT: number;
  knockedBleedout: number;
  name: string;
  yaw: number;
  fallVy: number;
  strafeDir: number;
  strafeTimer: number;
  loadout: BotLoadout;
  weaponRarity: WeaponRarity;
  role: BotRole;
  squadId: number;
  /** When true, crawl toward a knocked squadmate instead of normal patrol. */
  crawlToSquadmate: boolean;
  /** True after this bot has used its one reboot-van respawn this life. */
  rebootUsed: boolean;
  /** Seconds spent inside reboot van radius while knocked (resets when leaving). */
  vanRebootTimer: number;
  /** Mesh used as hit-target by player raycaster (the torso aggregate). */
  hitMesh: THREE.Object3D;
  stepDistAcc: number;
  baseTorsoEmissiveIntensity: number;
  baseTorsoEmissive: THREE.Color;
  jumpVy: number;
  swingTimer: number;
  editTimer: number;
  animator: FpsCharacterAnimator;
  shotFiredThisFrame: boolean;
  seekCoverTimer: number;
  suppressedFireTimer: number;
  fireJitter: number;
  calloutCd: number;
  intentCalloutCd: number;
  sniperGlintCd: number;
  sniperGlintT: number;
  sniperShotPrimed: boolean;
  pushTelegraphCd: number;
  pushTelegraphT: number;
  buildTelegraphCd: number;
  pendingBuildT: number;
  pendingBuildTarget: THREE.Vector3 | null;
  pendingBuildRamp: boolean;
  presetIndex: number;
  emoteKind: BotEmoteKind | null;
  emoteT: number;
}

/** Fortnite-style AI bots with patrol/chase/shoot/death behavior. */
export class FpsBots {
  private bots: Bot[] = [];
  private nextId = 1;
  private grad: THREE.DataTexture;
  private hudLayer: HTMLDivElement;
  private disposed = false;
  private readonly scheduledTimeouts = new Set<number>();
  private readonly tracerCleanups = new Set<() => void>();
  private bullets: {
    mesh: THREE.Mesh;
    vel: THREE.Vector3;
    life: number;
    target: THREE.Vector3;
    damageDelivered: boolean;
  }[] = [];

  private onBotElim?: (
    killer: string,
    victim: string,
    elimPos: { x: number; z: number },
    weaponKind?: WeaponKind,
  ) => void;
  private onBotChatter?: (botName: string, quip: string) => void;
  private onBotSquadCallout?: (botName: string, quip: string, kind: BotSquadTacticalKind) => void;
  private onBuildDamage?: (object: THREE.Object3D, amount: number) => void;
  private chatterCd = 0;
  private chatterInterval = CHATTER_MIN_INTERVAL + Math.random() * (CHATTER_MAX_INTERVAL - CHATTER_MIN_INTERVAL);
  private readonly botBuilds: FpsBotBuilds;
  private hitChanceBonus = 0;
  private supplyDropTarget: { x: number; z: number } | null = null;
  private llamaTarget: { x: number; z: number } | null = null;
  private readonly squadFireCooldowns = new Map<number, number>();
  private lastPlayerPos: THREE.Vector3 | null = null;
  private playerStationaryTimer = 0;
  private pressureMultiplier = 1;
  private endgameMode = false;
  private reactionTimeSec = 0.45;
  private killLeaderBotName: string | null = null;
  private readonly botMoveOverrides = new Map<number, { x: number; z: number; storm: boolean }>();
  private readonly botCoverTargets = new Map<number, THREE.Vector3>();
  private playerSniperGlintReactCd = 0;

  constructor(
    private scene: THREE.Scene,
    private sceneRoot: THREE.Group,
    spawnPoints: { x: number; z: number; hue?: number }[],
    opts: {
      onBotElim?: (
        killer: string,
        victim: string,
        elimPos: { x: number; z: number },
        weaponKind?: WeaponKind,
      ) => void;
      onBotChatter?: (botName: string, quip: string) => void;
      onBotSquadCallout?: (botName: string, quip: string, kind: BotSquadTacticalKind) => void;
      onBuildDamage?: (object: THREE.Object3D, amount: number) => void;
      hitChanceBonus?: number;
    } | undefined,
    private readonly world: CANNON.World,
    private readonly floorMat: CANNON.Material,
  ) {
    this.onBotElim = opts?.onBotElim;
    this.onBotChatter = opts?.onBotChatter;
    this.onBotSquadCallout = opts?.onBotSquadCallout;
    this.onBuildDamage = opts?.onBuildDamage;
    this.hitChanceBonus = Math.max(0, opts?.hitChanceBonus ?? 0);
    this.botBuilds = new FpsBotBuilds(world, floorMat, sceneRoot);
    this.grad = createToonGradientMap();
    this.hudLayer = document.createElement('div');
    this.hudLayer.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:30;overflow:hidden';
    document.body.appendChild(this.hudLayer);

    const spawns = spawnPoints.slice(0, MAX_BOTS);
    for (let i = 0; i < spawns.length; i++) {
      const p = spawns[i]!;
      const squadId = Math.floor(i / SQUAD_SIZE);
      this.spawnBot(p.x, p.z, p.hue ?? Math.random(), squadId);
    }
  }

  private scheduleTimeout(fn: () => void, ms: number): void {
    const id = window.setTimeout(() => {
      this.scheduledTimeouts.delete(id);
      if (!this.disposed) fn();
    }, ms);
    this.scheduledTimeouts.add(id);
  }

  private disposeObjectTree(root: THREE.Object3D): void {
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      geometries.add(object.geometry);
      const mat = object.material;
      if (Array.isArray(mat)) mat.forEach((m) => materials.add(m));
      else materials.add(mat);
    });
    for (const geometry of geometries) geometry.dispose();
    for (const material of materials) material.dispose();
  }

  setOnBotElim(
    cb: (
      killer: string,
      victim: string,
      elimPos: { x: number; z: number },
      weaponKind?: WeaponKind,
    ) => void,
  ): void {
    this.onBotElim = cb;
  }

  setOnBotChatter(cb: (botName: string, quip: string) => void): void {
    this.onBotChatter = cb;
  }

  setOnBotSquadCallout(cb: (botName: string, quip: string, kind: BotSquadTacticalKind) => void): void {
    this.onBotSquadCallout = cb;
  }

  setReactionTime(seconds: number): void {
    this.reactionTimeSec = Math.max(0.05, seconds);
  }

  setKillLeaderBotName(name: string | null): void {
    this.killLeaderBotName = name && name !== 'You' ? name : null;
    for (const bot of this.bots) {
      this.refreshBotNameTag(bot);
    }
  }

  emitSquadTacticalCallout(botName: string, kind: BotSquadTacticalKind): void {
    const lines = BOT_SQUAD_TACTICAL_CALLOUTS[kind];
    const message = lines[Math.floor(Math.random() * lines.length)]!;
    if (this.onBotSquadCallout) {
      this.onBotSquadCallout(botName, message, kind);
    } else if (this.onBotChatter) {
      this.onBotChatter(botName, message);
    }
  }

  setBotCoverTarget(liveBotIndex: number, pos: THREE.Vector3): void {
    this.botCoverTargets.set(liveBotIndex, pos.clone());
  }

  setBotMoveOverride(liveBotIndex: number, x: number, z: number, storm = false): void {
    this.botMoveOverrides.set(liveBotIndex, { x, z, storm });
  }

  clearBotMoveOverride(liveBotIndex: number): void {
    this.botMoveOverrides.delete(liveBotIndex);
  }

  healBotByIndex(liveBotIndex: number, amount: number): void {
    const liveBots = this.getLivingBots();
    const entry = liveBots[liveBotIndex];
    if (!entry) return;
    const bot = this.bots.find((b) => b.id === entry.id);
    if (!bot || bot.state === 'dead' || bot.state === 'knocked') return;
    bot.hp = Math.min(MAX_HP, bot.hp + amount);
  }

  upgradeBotWeaponByIndex(liveBotIndex: number, kind: WeaponKind, rarity: WeaponRarity): void {
    const liveBots = this.getLivingBots();
    const entry = liveBots[liveBotIndex];
    if (!entry) return;
    const bot = this.bots.find((b) => b.id === entry.id);
    if (!bot || bot.state === 'dead') return;
    const mappedLoadout = this.mapWeaponKindToLoadout(kind);
    if (mappedLoadout) {
      bot.loadout = mappedLoadout;
      bot.weaponRarity = rarity;
      this.refreshBotNameTag(bot);
    }
  }

  private mapWeaponKindToLoadout(kind: WeaponKind): BotLoadout | null {
    switch (kind) {
      case 'ar':
        return 'ar';
      case 'shotgun':
        return 'shotgun';
      case 'smg':
        return 'smg';
      case 'sniper':
        return 'sniper';
      case 'pistol':
        return 'smg';
      default: {
        const _exhaustive: never = kind;
        return _exhaustive;
      }
    }
  }

  private refreshBotNameTag(bot: Bot): void {
    const profile = BOT_LOADOUT_PROFILES[bot.loadout];
    const crown = bot.name === this.killLeaderBotName ? '👑 ' : '';
    const rarityTag = bot.weaponRarity === 'common' ? '' : ` · ${RARITY_LABELS[bot.weaponRarity]}`;
    const loadoutTag = profile.tag ? ` · ${profile.tag}` : '';
    bot.nameTag.textContent = `${crown}${bot.name}${loadoutTag}${rarityTag}`;
    if (bot.name === this.killLeaderBotName) {
      bot.nameTag.style.boxShadow = '0 0 0 2px #ffd54a, 0 0 14px rgba(255,213,74,0.55), 0 2px 8px rgba(0,0,0,0.45)';
    }
  }

  private isKillLeader(bot: Bot): boolean {
    return bot.name === this.killLeaderBotName;
  }

  public damageBotWall(object: THREE.Object3D, amount: number): boolean {
    return this.botBuilds.damageWall(object, amount);
  }

  public getBotBuildHp(object: THREE.Object3D): { hp: number; maxHp: number } | null {
    return this.botBuilds.getWallHp(object);
  }

  setHitChanceBonus(bonus: number): void {
    this.hitChanceBonus = bonus;
  }

  setPressureMultiplier(multiplier: number): void {
    this.pressureMultiplier = Math.max(0.9, Math.min(1.08, multiplier));
  }

  setEndgameMode(active: boolean): void {
    this.endgameMode = active;
  }

  /** Mid-game reinforcement wave — spawns fresh bots near the anchor point. */
  spawnReinforcementWave(nearX: number, nearZ: number, count = REINFORCEMENT_WAVE_SIZE): number {
    let spawned = 0;
    for (let i = 0; i < count; i++) {
      if (this.bots.length >= REINFORCEMENT_MAX_TOTAL) break;
      const angle = Math.random() * Math.PI * 2;
      const dist = 32 + Math.random() * 22;
      const x = nearX + Math.cos(angle) * dist;
      const z = nearZ + Math.sin(angle) * dist;
      const squadId = Math.floor(this.bots.length / SQUAD_SIZE);
      this.spawnBot(x, z, Math.random(), squadId);
      spawned += 1;
    }
    return spawned;
  }

  /** Trim excess bots during final circles, keeping those nearest the anchor. */
  enforceFinalCircleBotCap(maxAlive: number, nearX: number, nearZ: number): number {
    const living = this.bots.filter((bot) => bot.state !== 'dead');
    if (living.length <= maxAlive) return 0;

    const sorted = [...living].sort((a, b) => {
      const da = Math.hypot(a.root.position.x - nearX, a.root.position.z - nearZ);
      const db = Math.hypot(b.root.position.x - nearX, b.root.position.z - nearZ);
      return da - db;
    });

    let removed = 0;
    for (let i = maxAlive; i < sorted.length; i++) {
      this.killBot(sorted[i]!, 'Storm');
      removed += 1;
    }
    return removed;
  }

  setSupplyDropTarget(x: number | null, z: number | null): void {
    if (x == null || z == null) this.supplyDropTarget = null;
    else this.supplyDropTarget = { x, z };
  }

  setLlamaTarget(x: number | null, z: number | null): void {
    if (x == null || z == null) this.llamaTarget = null;
    else this.llamaTarget = { x, z };
  }

  /** Impulse knockback for shockwave grenades — no damage. */
  applyShockwaveKnockback(cx: number, cz: number, radius: number, force: number): void {
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      const dx = bot.root.position.x - cx;
      const dz = bot.root.position.z - cz;
      const d = Math.hypot(dx, dz);
      if (d >= radius || d < 0.01) continue;
      const scale = (1 - d / radius) * force * 0.38;
      bot.root.position.x += (dx / d) * scale;
      bot.root.position.z += (dz / d) * scale;
    }
  }

  private botHitChance(dist: number): number {
    return Math.max(0, Math.min(1, (baseBotHitChance(dist) + this.hitChanceBonus) * this.pressureMultiplier));
  }

  private pickBotRole(spawnIndex: number): BotRole {
    return BOT_ROLE_ORDER[spawnIndex % BOT_ROLE_ORDER.length]!;
  }

  private spawnBot(x: number, z: number, hueSeed: number, squadId: number): void {
    const id = this.nextId++;
    const presetIndex = this.bots.length;
    const preset = BOT_PRESETS[presetIndex % BOT_PRESETS.length]!;
    const loadout = pickBotLoadout();
    const profile = BOT_LOADOUT_PROFILES[loadout];
    const role = this.pickBotRole(presetIndex);
    const root = new THREE.Group();
    root.position.set(x, 0, z);
    this.sceneRoot.add(root);

    const body = new THREE.Group();
    body.position.y = 0;
    root.add(body);

    const armorColor = new THREE.Color().setHSL(preset.hue, 0.65, 0.45);
    const accentColor = new THREE.Color().setHSL((preset.hue * 0.7 + 0.5) % 1, 0.65, 0.58);
    const skin = new THREE.Color().setHSL(0.07, 0.55, 0.62);

    const torsoMat = toonMat(armorColor.getHex(), this.grad, {
      emissive: armorColor.clone().multiplyScalar(0.22).getHex(),
      emissiveIntensity: 0.18,
    });
    const accentMat = toonMat(accentColor.getHex(), this.grad, {
      emissive: accentColor.clone().multiplyScalar(0.45).getHex(),
      emissiveIntensity: 0.32,
    });
    const roleAccentColor = this.getRoleAccentColor(role);
    const roleAccentMat = toonMat(roleAccentColor, this.grad, {
      emissive: roleAccentColor,
      emissiveIntensity: 0.42,
    });
    const skinMat = toonMat(skin.getHex(), this.grad, {
      emissive: 0x442211,
      emissiveIntensity: 0.06,
    });
    const helmetMat = toonMat(0x202830, this.grad, {
      emissive: 0x0a1018,
      emissiveIntensity: 0.06,
    });

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.95, 0.42), torsoMat);
    torso.position.y = 1.25;
    torso.castShadow = true;
    torso.receiveShadow = true;
    torso.name = 'torso';
    body.add(torso);

    const beltAccent = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.12, 0.46), accentMat);
    beltAccent.position.y = 0.84;
    beltAccent.castShadow = true;
    body.add(beltAccent);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24, 14, 12), skinMat);
    head.position.y = 1.92;
    head.castShadow = true;
    head.scale.set(preset.headScaleX, preset.headScaleY, 1.0);
    body.add(head);

    const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.27, 14, 10, 0, Math.PI * 2, 0, 1.2), helmetMat);
    helmet.position.y = 1.96;
    helmet.castShadow = true;
    body.add(helmet);

    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.07, 0.05),
      new THREE.MeshStandardMaterial({
        color: 0x55ffe0,
        emissive: 0x33ffe0,
        emissiveIntensity: 1.4,
        metalness: 0.6,
        roughness: 0.2,
      }),
    );
    visor.position.set(0, 1.94, 0.22);
    body.add(visor);

    const armGeo = new THREE.CapsuleGeometry(0.1, 0.55, 4, 8);
    const leftArm = new THREE.Mesh(armGeo, torsoMat);
    leftArm.position.set(-0.46, 1.42, 0);
    leftArm.castShadow = true;
    leftArm.name = 'leftArm';
    body.add(leftArm);
    const rightArm = new THREE.Mesh(armGeo, torsoMat);
    rightArm.position.set(0.46, 1.42, 0);
    rightArm.castShadow = true;
    rightArm.name = 'rightArm';
    body.add(rightArm);

    const legGeo = new THREE.CapsuleGeometry(0.13, 0.6, 4, 8);
    const legMat = toonMat(armorColor.clone().multiplyScalar(0.7).getHex(), this.grad);
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.2, 0.5, 0);
    leftLeg.castShadow = true;
    leftLeg.name = 'leftLeg';
    body.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, legMat);
    rightLeg.position.set(0.2, 0.5, 0);
    rightLeg.castShadow = true;
    rightLeg.name = 'rightLeg';
    body.add(rightLeg);

    const gun = this.buildBotGun(loadout);
    gun.position.set(0.4, 1.42, 0.35);
    body.add(gun);

    // Accessories from preset
    if (preset.accessory === 'cap') {
      const capMesh = new THREE.Mesh(
        new THREE.CylinderGeometry(0.24, 0.26, 0.06, 8),
        new THREE.MeshStandardMaterial({ color: 0x222222 }),
      );
      capMesh.position.set(0, 2.06, 0);
      body.add(capMesh);
    } else if (preset.accessory === 'backpack') {
      const bpMesh = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.26, 0.08),
        new THREE.MeshStandardMaterial({ color: 0x444444 }),
      );
      bpMesh.position.set(0, 1.25, -0.18 - 0.21);
      body.add(bpMesh);
    }

    this.addBotRoleAccent(body, role, roleAccentMat);

    const squadColor = getBotSquadColor(squadId);
    const nameTag = document.createElement('div');
    nameTag.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);padding:2px 7px;border-radius:4px;background:rgba(8,12,20,0.75);color:#fff;font:700 10px system-ui,sans-serif;text-shadow:0 1px 3px #000;pointer-events:none;white-space:nowrap;border:1px solid rgba(255,255,255,0.18)';
    nameTag.style.boxShadow = `0 0 0 2px ${squadColor}, 0 2px 8px rgba(0,0,0,0.45)`;
    const name = randomElimName();
    nameTag.textContent = profile.tag ? `${name} · ${profile.tag}` : name;
    this.hudLayer.appendChild(nameTag);

    const hpBar = document.createElement('div');
    hpBar.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);width:48px;height:5px;border-radius:3px;background:rgba(0,0,0,0.55);border:1px solid rgba(255,255,255,0.18);overflow:hidden;pointer-events:none';
    const hpFill = document.createElement('div');
    hpFill.style.cssText = 'height:100%;width:100%;background:linear-gradient(90deg,#36e36b,#9bff6a)';
    hpBar.appendChild(hpFill);
    this.hudLayer.appendChild(hpBar);

    // Aggregate hit mesh: invisible bounding box used for player raycast hits.
    // (lets us track hits without slow per-limb hierarchy walks)
    const hitMesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 1.95, 0.55),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    hitMesh.position.y = 1.0;
    hitMesh.userData.botId = id;
    body.add(hitMesh);

    const bot: Bot = {
      id,
      root,
      body,
      head,
      torso,
      leftArm,
      rightArm,
      leftLeg,
      rightLeg,
      gun,
      hpBar,
      hpFill,
      nameTag,
      hp: MAX_HP,
      shield: role === 'support' ? 50 : 0,
      state: 'patrol',
      patrolTarget: this.pickPatrolPoint(x, z),
      patrolWait: 0,
      shootCd: INITIAL_AGGRO_DELAY + Math.random() * 2.0,
      walkPhase: Math.random() * Math.PI * 2,
      velocity: new THREE.Vector3(),
      deathT: 0,
      knockedBleedout: 0,
      name,
      yaw: Math.random() * Math.PI * 2,
      fallVy: 0,
      strafeDir: 0,
      strafeTimer: 0.5 + Math.random(),
      loadout,
      weaponRarity: 'common',
      role,
      squadId,
      crawlToSquadmate: false,
      rebootUsed: false,
      vanRebootTimer: 0,
      hitMesh,
      stepDistAcc: Math.random() * 0.6,
      baseTorsoEmissiveIntensity: 0.18,
      baseTorsoEmissive: (torsoMat as THREE.MeshToonMaterial).emissive.clone(),
      jumpVy: 0,
      swingTimer: 0,
      editTimer: 0,
      animator: new FpsCharacterAnimator(body),
      shotFiredThisFrame: false,
      seekCoverTimer: 0,
      suppressedFireTimer: 0,
      fireJitter: 0.05 + ((id * 0.173) % 0.45),
      calloutCd: 2 + ((id * 0.41) % 3),
      intentCalloutCd: 1.5 + ((id * 0.29) % 2.5),
      sniperGlintCd: 0.75 + ((id * 0.37) % 1.8),
      sniperGlintT: 0,
      sniperShotPrimed: false,
      pushTelegraphCd: 0.8 + ((id * 0.23) % 1.2),
      pushTelegraphT: 0,
      buildTelegraphCd: 1.0 + ((id * 0.31) % 1.6),
      pendingBuildT: 0,
      pendingBuildTarget: null,
      pendingBuildRamp: false,
      presetIndex,
      emoteKind: null,
      emoteT: 0,
    };
    this.bots.push(bot);
  }

  private getRoleAccentColor(role: BotRole): number {
    switch (role) {
      case 'rusher':
        return 0xff3b2f;
      case 'builder':
        return 0x42e8ff;
      case 'flanker':
        return 0x151820;
      case 'sniper':
        return 0x88ccff;
      case 'support':
        return 0x43e66b;
    }
  }

  private addBotRoleAccent(body: THREE.Group, role: BotRole, mat: THREE.Material): void {
    const add = (mesh: THREE.Mesh) => {
      mesh.castShadow = true;
      body.add(mesh);
    };

    if (role === 'rusher') {
      const pad = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.1, 0.24), mat);
      pad.position.set(-0.49, 1.62, 0.02);
      pad.rotation.z = 0.18;
      add(pad);
      return;
    }

    if (role === 'builder') {
      const pack = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.34, 0.1), mat);
      pack.position.set(0, 1.35, -0.44);
      add(pack);
      const blueprint = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.16, 0.02), mat);
      blueprint.position.set(0.33, 1.24, 0.24);
      blueprint.rotation.z = -0.18;
      add(blueprint);
      return;
    }

    if (role === 'flanker') {
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.78, 0.03), mat);
      stripe.position.set(0.42, 1.27, 0.23);
      add(stripe);
      return;
    }

    if (role === 'sniper') {
      const scope = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.18, 8), mat);
      scope.rotation.z = Math.PI / 2;
      scope.position.set(0.21, 1.96, 0.25);
      add(scope);
      return;
    }

    const pouch = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.16, 0.09), mat);
    pouch.position.set(-0.28, 0.85, 0.26);
    add(pouch);
  }

  private buildBotGun(loadout: BotLoadout): THREE.Group {
    const profile = BOT_LOADOUT_PROFILES[loadout];
    const g = new THREE.Group();
    const bodyLen = loadout === 'shotgun' ? 0.42 : loadout === 'smg' ? 0.38 : 0.5;
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(loadout === 'smg' ? 0.09 : 0.1, 0.12, bodyLen),
      new THREE.MeshStandardMaterial({
        color: profile.gunColor,
        roughness: 0.4,
        metalness: 0.55,
        emissive: 0x111111,
      }),
    );
    body.position.z = 0;
    body.castShadow = true;
    g.add(body);
    const barrelLen = loadout === 'shotgun' ? 0.28 : loadout === 'smg' ? 0.32 : 0.4;
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(
        loadout === 'shotgun' ? 0.04 : 0.032,
        loadout === 'shotgun' ? 0.048 : 0.038,
        barrelLen,
        8,
      ),
      new THREE.MeshStandardMaterial({ color: 0x808088, metalness: 0.75, roughness: 0.3 }),
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = loadout === 'shotgun' ? 0.2 : 0.28;
    g.add(barrel);
    const accent = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.06, 0.1),
      new THREE.MeshStandardMaterial({
        color: profile.accentColor,
        emissive: profile.accentColor,
        emissiveIntensity: 0.6,
      }),
    );
    accent.position.set(0, 0.07, 0.05);
    accent.userData.sniperGlint = true;
    g.add(accent);
    return g;
  }

  private pickPatrolPoint(cx: number, cz: number): THREE.Vector3 {
    const r = 6 + Math.random() * 18;
    const ang = Math.random() * Math.PI * 2;
    const nx = Math.max(-46, Math.min(46, cx + Math.cos(ang) * r));
    const nz = Math.max(-46, Math.min(46, cz + Math.sin(ang) * r));
    return new THREE.Vector3(nx, 0, nz);
  }

  /** Returns the hit meshes used for player raycasting. */
  getHitMeshes(): THREE.Object3D[] {
    return this.bots.filter((b) => b.state !== 'dead').map((b) => b.hitMesh);
  }

  getBotBuildMeshes(): THREE.Object3D[] {
    return this.botBuilds.getMeshesForRaycast();
  }

  damageBotBuildFromHit(object: THREE.Object3D, amount: number): boolean {
    return this.botBuilds.damageWall(object, amount);
  }

  isBotBuildObject(object: THREE.Object3D): boolean {
    return this.botBuilds.isBotBuildObject(object);
  }

  /** Raycast along a shot line and damage the first bot wall hit. Returns true if destroyed. */
  raycastDamageBotBuild(
    origin: THREE.Vector3,
    target: THREE.Vector3,
    amount: number,
  ): boolean {
    const hit = this.findBotBuildHit(origin, target);
    if (!hit) return false;
    return this.botBuilds.damageWall(hit, amount);
  }

  /** First bot build mesh intersected along a shot line, if any. */
  findBotBuildHit(origin: THREE.Vector3, target: THREE.Vector3): THREE.Object3D | null {
    const meshes = this.botBuilds.getMeshesForRaycast();
    if (meshes.length === 0) return null;
    const dir = target.clone().sub(origin);
    const dist = dir.length();
    if (dist < 0.02) return null;
    dir.multiplyScalar(1 / dist);
    const raycaster = new THREE.Raycaster(origin, dir, 0, dist);
    const hits = raycaster.intersectObjects(meshes, false);
    return hits[0]?.object ?? null;
  }

  /** Look up bot by raycast object; walks parents to find botId. */
  findBotByHit(object: THREE.Object3D): { bot: Bot } | null {
    let cur: THREE.Object3D | null = object;
    while (cur) {
      const bid = cur.userData?.botId as number | undefined;
      if (typeof bid === 'number') {
        const bot = this.bots.find((b) => b.id === bid);
        if (bot) return { bot };
      }
      cur = cur.parent;
    }
    return null;
  }

  /** Apply damage to bot from a player shot at point. */
  damageBotByHit(
    object: THREE.Object3D,
    pt: THREE.Vector3,
    damage: number,
  ): BotHitResult | null {
    const found = this.findBotByHit(object);
    if (!found) return null;
    const bot = found.bot;
    if (bot.state === 'dead') return null;
    const pos = bot.root.position.clone();
    if (bot.state === 'knocked') {
      this.killBot(bot, 'You');
      return { killed: true, damage, name: bot.name, headshot: false, pos };
    }
    const headY = bot.root.position.y + 1.92;
    const headshot = pt.y > headY - 0.16;
    let dmg = headshot ? damage * 2.1 : damage;
    let shieldHit = false;
    if (bot.shield > 0) {
      const fromShield = Math.min(bot.shield, dmg);
      if (fromShield > 0) {
        bot.shield -= fromShield;
        dmg -= fromShield;
        shieldHit = true;
      }
    }
    const prevHp = bot.hp;
    if (dmg > 0) bot.hp -= dmg;
    const dealt = shieldHit ? (headshot ? damage * 2.1 : damage) : dmg;
    if (bot.hp <= 0) {
      this.knockBot(bot);
      return { killed: false, knocked: true, damage: dealt, name: bot.name, headshot, shieldHit, pos };
    }
    this.flashBotHurt(bot, dealt, prevHp);
    return { killed: false, damage: dealt, name: bot.name, headshot, shieldHit, pos };
  }

  /** Pickaxe melee — 2.5m forward cone; combo hit scales damage (finisher strongest). */
  damageBotInMelee(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    comboHit: 1 | 2 | 3 = 1,
  ): BotHitResult | null {
    const meleeDamage = comboHit === 3 ? 38 : comboHit === 2 ? 28 : 22;
    let best: { bot: Bot; dot: number } | null = null;
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      const toBot = new THREE.Vector3(
        bot.root.position.x - origin.x,
        bot.root.position.y + (bot.state === 'knocked' ? 0.35 : 1.2) - origin.y,
        bot.root.position.z - origin.z,
      );
      const dist = toBot.length();
      if (dist > MELEE_RANGE || dist < 0.02) continue;
      toBot.multiplyScalar(1 / dist);
      const dot = dir.dot(toBot);
      if (dot < MELEE_CONE_DOT) continue;
      if (!best || dot > best.dot) best = { bot, dot };
    }
    if (!best) return null;

    const bot = best.bot;
    const pos = bot.root.position.clone();
    if (bot.state === 'knocked') {
      this.killBot(bot, 'You');
      return { killed: true, damage: meleeDamage, name: bot.name, headshot: false, pos };
    }
    const prevHp = bot.hp;
    bot.hp -= meleeDamage;
    if (bot.hp <= 0) {
      this.knockBot(bot);
      return { killed: false, knocked: true, damage: meleeDamage, name: bot.name, headshot: false, pos };
    }
    this.flashBotHurt(bot, meleeDamage, prevHp);
    return { killed: false, damage: meleeDamage, name: bot.name, headshot: false, pos };
  }

  /** Radial explosion damage to living bots. */
  damageBotsInRadius(
    cx: number,
    cy: number,
    cz: number,
    radius: number,
    maxDamage: number,
    killerName = 'Explosion',
  ): BotHitResult[] {
    const hits: BotHitResult[] = [];
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      const tx = bot.root.position.x;
      const ty = bot.root.position.y + 1.05;
      const tz = bot.root.position.z;
      const dx = tx - cx;
      const dy = ty - cy;
      const dz = tz - cz;
      const d = Math.hypot(dx, dy, dz);
      if (d >= radius) continue;
      const falloff = 1 - d / radius;
      const dmg = maxDamage * falloff * falloff;
      const pos = bot.root.position.clone();
      const threat = new THREE.Vector3(cx, cy, cz);
      if (bot.state === 'knocked') {
        this.killBot(bot, killerName);
        hits.push({ killed: true, damage: dmg, name: bot.name, headshot: false, pos });
        continue;
      }
      const prevHp = bot.hp;
      bot.hp -= dmg;
      if (bot.hp <= 0) {
        this.knockBot(bot);
        hits.push({ killed: false, knocked: true, damage: dmg, name: bot.name, headshot: false, pos });
      } else {
        this.flashBotHurt(bot, dmg, prevHp);
        hits.push({ killed: false, damage: dmg, name: bot.name, headshot: false, pos });
      }
    }
    return hits;
  }

  /** Flat damage to all bots within radius (no falloff — stink cloud, etc.). */
  applyFlatRadiusDamage(
    cx: number,
    cy: number,
    cz: number,
    radius: number,
    damage: number,
    killerName = 'Stink',
  ): BotHitResult[] {
    const hits: BotHitResult[] = [];
    if (damage <= 0) return hits;
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      const tx = bot.root.position.x;
      const ty = bot.root.position.y + 1.05;
      const tz = bot.root.position.z;
      const dx = tx - cx;
      const dy = ty - cy;
      const dz = tz - cz;
      if (Math.hypot(dx, dy, dz) >= radius) continue;
      const pos = bot.root.position.clone();
      const threat = new THREE.Vector3(cx, cy, cz);
      if (bot.state === 'knocked') {
        this.killBot(bot, killerName);
        hits.push({ killed: true, damage, name: bot.name, headshot: false, pos });
        continue;
      }
      const prevHp = bot.hp;
      bot.hp -= damage;
      if (bot.hp <= 0) {
        this.knockBot(bot);
        hits.push({ killed: false, knocked: true, damage, name: bot.name, headshot: false, pos });
      } else {
        this.flashBotHurt(bot, damage, prevHp);
        hits.push({ killed: false, damage, name: bot.name, headshot: false, pos });
      }
    }
    return hits;
  }

  private findNearestUnopenedChest(
    bot: Bot,
    loot: FpsLoot,
  ): { id: string; x: number; z: number; dist: number } | null {
    const bpx = bot.root.position.x;
    const bpz = bot.root.position.z;
    let best: { id: string; x: number; z: number; dist: number } | null = null;
    for (const c of loot.getChestPositions()) {
      if (loot.isOpened(c.id)) continue;
      const dist = Math.hypot(c.x - bpx, c.z - bpz);
      if (!best || dist < best.dist) best = { ...c, dist };
    }
    return best;
  }

  private tryBotOpenChest(bot: Bot, loot: FpsLoot, chestId: string): void {
    if (!loot.openChestById(chestId)) return;
    bot.hp = Math.min(MAX_HP, bot.hp + CHEST_SHIELD_RESTORE);
  }

  private flashBotHurt(bot: Bot, damage = 0, prevHp = bot.hp + damage): void {
    const cracked = (prevHp >= 65 && bot.hp < 65) || (prevHp >= 35 && bot.hp < 35);
    const heavyDamage = damage >= BOT_HEAVY_DAMAGE || cracked;
    const coverPauseBonus = this.endgameMode ? 0.45 : 0;
    bot.seekCoverTimer = Math.max(bot.seekCoverTimer, (heavyDamage ? 3.0 : 2.5) + coverPauseBonus);
    if (heavyDamage) {
      bot.suppressedFireTimer = Math.max(bot.suppressedFireTimer, this.endgameMode ? 2.15 : 1.85);
      bot.shootCd = Math.max(bot.shootCd, (this.endgameMode ? 1.25 : 1.05) + bot.fireJitter);
      this.maybeBotRoleCallout(bot, cracked ? 'cracked' : 'retreat');
    }
    if (bot.torso.userData.uniqueMaterial !== true) {
      bot.torso.material = (bot.torso.material as THREE.MeshToonMaterial).clone();
      bot.torso.userData.uniqueMaterial = true;
    }
    const m = bot.torso.material as THREE.MeshToonMaterial;
    const origEmissive = m.emissive.clone();
    const origIntensity = m.emissiveIntensity;
    m.emissive.setHex(0xff4040);
    m.emissiveIntensity = 0.9;
    this.scheduleTimeout(() => {
      m.emissive.copy(origEmissive);
      m.emissiveIntensity = origIntensity;
    }, 110);
  }

  private maybeBotCallout(bot: Bot, message: string): void {
    if (Math.random() >= BOT_CALLOUT_CHANCE) return;
    this.emitBotCallout(bot, message);
  }

  private emitBotCallout(bot: Bot, message: string, cooldown = 8 + Math.random() * 5): boolean {
    if (!this.onBotChatter || bot.calloutCd > 0) return false;
    this.onBotChatter(bot.name, message);
    bot.calloutCd = cooldown;
    return true;
  }

  private maybeBotRoleCallout(bot: Bot, kind: BotCalloutKind): void {
    const lines = BOT_ROLE_CALLOUTS[bot.role][kind];
    this.maybeBotCallout(bot, lines[Math.floor(Math.random() * lines.length)]!);
  }

  private telegraphBotIntent(bot: Bot, distToPlayer: number): void {
    if (bot.intentCalloutCd > 0) return;
    if (bot.role === 'rusher' && distToPlayer <= 15) {
      const lines = ['Rusher closing fast!', 'Rusher is taking space!'];
      if (this.emitBotCallout(bot, lines[Math.floor(Math.random() * lines.length)]!, BOT_INTENT_CALLOUT_COOLDOWN)) {
        bot.intentCalloutCd = BOT_INTENT_CALLOUT_COOLDOWN;
      }
      return;
    }
    if (bot.role === 'builder' && distToPlayer <= BOT_COMBAT_BUILD_RANGE) {
      const lines = ['Builder setting cover!', 'Builder about to box up!'];
      if (this.emitBotCallout(bot, lines[Math.floor(Math.random() * lines.length)]!, BOT_INTENT_CALLOUT_COOLDOWN)) {
        bot.intentCalloutCd = BOT_INTENT_CALLOUT_COOLDOWN;
      }
    }
  }

  private telegraphPushWindow(bot: Bot, distToPlayer: number): void {
    if (bot.pushTelegraphCd > 0 || bot.pushTelegraphT > 0 || distToPlayer > 24) return;
    if (bot.role !== 'rusher' && bot.role !== 'flanker') return;
    bot.pushTelegraphT = BOT_PUSH_TELEGRAPH_WINDOW;
    bot.pushTelegraphCd = BOT_PUSH_TELEGRAPH_COOLDOWN + bot.fireJitter;
    const lines = bot.role === 'flanker'
      ? ['Wide swing coming!', 'Flanker winding wide!']
      : ['Hard push coming!', 'Rusher winding up!'];
    this.emitBotCallout(bot, lines[Math.floor(Math.random() * lines.length)]!, BOT_INTENT_CALLOUT_COOLDOWN);
  }

  private requestBotBuildWindow(bot: Bot, playerPos: THREE.Vector3, withRamp: boolean, force = false): boolean {
    if (bot.pendingBuildT > 0 || bot.pendingBuildTarget) return false;
    if (!force && bot.buildTelegraphCd > 0) return false;
    bot.pendingBuildT = BOT_BUILD_TELEGRAPH_WINDOW;
    bot.pendingBuildTarget = playerPos.clone();
    bot.pendingBuildRamp = withRamp;
    bot.buildTelegraphCd = BOT_BUILD_TELEGRAPH_COOLDOWN + bot.fireJitter;
    bot.suppressedFireTimer = Math.max(bot.suppressedFireTimer, BOT_BUILD_TELEGRAPH_WINDOW + BOT_BUILD_RECOVERY_PAUSE);
    bot.shootCd = Math.max(bot.shootCd, BOT_BUILD_TELEGRAPH_WINDOW + BOT_BUILD_RECOVERY_PAUSE);
    this.maybeBotRoleCallout(bot, 'build');
    return true;
  }

  private resolvePendingBotBuild(bot: Bot, buildCoverEvents?: string[]): void {
    if (!bot.pendingBuildTarget || bot.pendingBuildT > 0) return;
    const target = bot.pendingBuildTarget;
    const placedWall = this.botBuilds.tryPlaceWall(
      bot.id,
      bot.root.position.x,
      bot.root.position.z,
      target.x,
      target.z,
    );
    if (placedWall && bot.pendingBuildRamp) {
      this.botBuilds.tryPlaceRamp(
        bot.id,
        bot.root.position.x,
        bot.root.position.z,
        target.x,
        target.z,
      );
    }
    if (placedWall) {
      buildCoverEvents?.push(bot.name);
      bot.suppressedFireTimer = Math.max(bot.suppressedFireTimer, BOT_BUILD_RECOVERY_PAUSE);
      bot.shootCd = Math.max(bot.shootCd, BOT_BUILD_RECOVERY_PAUSE + bot.fireJitter * 0.4);
    }
    bot.pendingBuildTarget = null;
    bot.pendingBuildRamp = false;
  }

  private telegraphSniperAim(bot: Bot): void {
    if (bot.sniperGlintT > 0) return;
    const canCallout = bot.sniperGlintCd <= 0;
    bot.sniperGlintT = BOT_SNIPER_GLINT_DURATION;
    bot.sniperShotPrimed = true;
    if (canCallout) {
      bot.sniperGlintCd = BOT_SNIPER_GLINT_COOLDOWN + bot.fireJitter;
    }
    bot.shootCd = Math.max(bot.shootCd, BOT_SNIPER_GLINT_DURATION);
    if (canCallout) {
      this.emitBotCallout(bot, 'Sniper glint - break sight!', BOT_INTENT_CALLOUT_COOLDOWN);
    }
  }

  private updateSniperGlint(bot: Bot, dt: number): void {
    bot.sniperGlintT = Math.max(0, bot.sniperGlintT - dt);
    const pulse = bot.sniperGlintT > 0
      ? 1 + Math.sin((1 - bot.sniperGlintT / BOT_SNIPER_GLINT_DURATION) * Math.PI) * 2.8
      : 1;
    bot.gun.traverse((object) => {
      if (!(object instanceof THREE.Mesh) || object.userData.sniperGlint !== true) return;
      const mat = object.material;
      if (!(mat instanceof THREE.MeshStandardMaterial)) return;
      const base = typeof object.userData.baseGlintIntensity === 'number'
        ? object.userData.baseGlintIntensity
        : mat.emissiveIntensity;
      object.userData.baseGlintIntensity = base;
      mat.emissiveIntensity = base * pulse;
    });
  }

  private knockBot(bot: Bot, attackerName?: string): void {
    bot.state = 'knocked';
    bot.hp = 0;
    bot.knockedBleedout = KNOCK_BLEEDOUT;
    bot.vanRebootTimer = 0;
    bot.shootCd = 0.5;
    bot.strafeDir = 0;
    bot.sniperShotPrimed = false;
    bot.sniperGlintT = 0;
    bot.pendingBuildT = 0;
    bot.pendingBuildTarget = null;
    bot.pendingBuildRamp = false;
    bot.emoteKind = null;
    bot.emoteT = 0;
    this.applyKnockedPose(bot);
    bot.nameTag.style.color = '#ff9a3c';
    bot.nameTag.style.borderColor = 'rgba(255,154,60,0.55)';
    bot.hpFill.style.background = 'linear-gradient(90deg,#ff9a3c,#ffb86a)';
    bot.hpFill.style.width = '0%';
    if (attackerName) {
      const attacker = this.findBotByName(attackerName);
      if (attacker && attacker.state !== 'dead' && attacker.state !== 'knocked') {
        this.playBotEmote(attacker, 'taunt');
      }
    }
    for (const other of this.bots) {
      if (
        other.id !== bot.id &&
        other.squadId === bot.squadId &&
        other.state !== 'dead' &&
        other.state !== 'knocked' &&
        Math.random() < SQUADMATE_CRAWL_CHANCE
      ) {
        other.crawlToSquadmate = true;
        break;
      }
    }
  }

  private applyKnockedPose(bot: Bot): void {
    bot.body.rotation.x = Math.PI / 2;
    bot.body.position.y = 0.28;
    bot.gun.visible = false;
    bot.leftArm.rotation.x = 0.15;
    bot.rightArm.rotation.x = -0.1;
    bot.leftLeg.rotation.x = 0.05;
    bot.rightLeg.rotation.x = -0.05;
  }

  private resetStandingPose(bot: Bot): void {
    bot.body.rotation.x = 0;
    bot.body.rotation.z = 0;
    bot.body.position.y = 0;
    bot.leftArm.rotation.x = 0;
    bot.rightArm.rotation.x = 0;
    bot.leftLeg.rotation.x = 0;
    bot.rightLeg.rotation.x = 0;
  }

  private rebootBotFromVan(bot: Bot): void {
    bot.rebootUsed = true;
    bot.vanRebootTimer = 0;
    bot.state = 'patrol';
    bot.hp = MAX_HP;
    bot.knockedBleedout = 0;
    bot.shootCd = 0.8;
    bot.crawlToSquadmate = false;
    bot.sniperShotPrimed = false;
    bot.sniperGlintT = 0;
    bot.pendingBuildT = 0;
    bot.pendingBuildTarget = null;
    bot.pendingBuildRamp = false;
    bot.patrolTarget = this.pickPatrolPoint(REBOOT_VAN_X, REBOOT_VAN_Z);
    bot.root.position.set(REBOOT_VAN_X, 0, REBOOT_VAN_Z);
    this.resetStandingPose(bot);
    bot.gun.visible = true;
    bot.nameTag.style.color = '#fff';
    bot.nameTag.style.borderColor = 'rgba(255,255,255,0.18)';
    bot.hpFill.style.background = 'linear-gradient(90deg,#36e36b,#9bff6a)';
    bot.hpFill.style.width = '100%';
  }

  private killBot(bot: Bot, killerName?: string): void {
    bot.state = 'dead';
    bot.deathT = 0;
    bot.fallVy = 1.2;
    bot.sniperShotPrimed = false;
    bot.sniperGlintT = 0;
    bot.pendingBuildT = 0;
    bot.pendingBuildTarget = null;
    bot.pendingBuildRamp = false;
    bot.emoteKind = null;
    bot.emoteT = 0;
    bot.hpBar.style.display = 'none';
    bot.nameTag.style.display = 'none';
    for (const other of this.bots) {
      if (other.squadId === bot.squadId) other.crawlToSquadmate = false;
    }
    if (killerName) {
      const killer = this.findBotByName(killerName);
      if (killer && killer.state !== 'dead' && killer.state !== 'knocked') {
        this.playBotEmote(killer, 'victory');
      }
    }
    if (this.onBotElim) {
      const killerBot = killerName ? this.findBotByName(killerName) : undefined;
      const weaponKind = killerBot?.loadout as WeaponKind | undefined;
      this.onBotElim(killerName ?? '', bot.name, {
        x: bot.root.position.x,
        z: bot.root.position.z,
      }, weaponKind);
    }
  }

  private findBotByName(name: string): Bot | undefined {
    return this.bots.find((bot) => bot.name === name);
  }

  private playBotEmote(bot: Bot, kind: BotEmoteKind): void {
    bot.emoteKind = kind;
    bot.emoteT = getBotEmoteDuration(kind);
    bot.state = 'patrol';
    bot.shootCd = Math.max(bot.shootCd, bot.emoteT);
    bot.gun.visible = false;
  }

  private applyBotEmotePose(bot: Bot, dt: number): void {
    if (!bot.emoteKind || bot.emoteT <= 0) return;
    bot.emoteT = Math.max(0, bot.emoteT - dt);
    const t = performance.now() * 0.001;

    if (bot.emoteKind === 'victory') {
      const bounce = Math.sin(t * 8) * 0.08;
      bot.body.position.y = bounce;
      bot.leftArm.rotation.x = -1.35 + Math.sin(t * 6) * 0.12;
      bot.rightArm.rotation.x = -1.35 + Math.sin(t * 6 + 0.6) * 0.12;
      bot.leftLeg.rotation.x = Math.sin(t * 5) * 0.18;
      bot.rightLeg.rotation.x = -Math.sin(t * 5) * 0.18;
      bot.torso.rotation.z = Math.sin(t * 4) * 0.06;
    } else {
      bot.body.position.y = 0;
      bot.leftArm.rotation.x = -0.35;
      bot.rightArm.rotation.x = -1.05 + Math.sin(t * 5) * 0.08;
      bot.torso.rotation.z = 0.12;
      bot.head.rotation.z = 0.18;
    }

    if (bot.emoteT <= 0) {
      bot.emoteKind = null;
      bot.gun.visible = true;
      bot.body.position.y = 0;
      bot.torso.rotation.z = 0;
      bot.head.rotation.z = 0;
      bot.leftArm.rotation.x = 0;
      bot.rightArm.rotation.x = 0;
      bot.leftLeg.rotation.x = 0;
      bot.rightLeg.rotation.x = 0;
    }
  }

  private updateRadioChatter(dt: number): void {
    this.chatterCd = Math.max(0, this.chatterCd - dt);
    this.chatterInterval -= dt;
    if (this.chatterInterval > 0 || this.chatterCd > 0 || !this.onBotChatter) return;

    const alive = this.bots.filter((b) => b.state !== 'dead' && b.state !== 'knocked');
    if (alive.length === 0) {
      this.chatterInterval = CHATTER_MIN_INTERVAL + Math.random() * (CHATTER_MAX_INTERVAL - CHATTER_MIN_INTERVAL);
      return;
    }

    const bot = alive[Math.floor(Math.random() * alive.length)]!;
    this.onBotChatter(bot.name, randomBotRadioQuip());
    this.chatterCd = CHATTER_COOLDOWN;
    this.chatterInterval = CHATTER_MIN_INTERVAL + Math.random() * (CHATTER_MAX_INTERVAL - CHATTER_MIN_INTERVAL);
  }

  private damageBotFromBot(victim: Bot, damage: number, killer: Bot): boolean {
    if (victim.state === 'dead') return false;
    if (victim.state === 'knocked') {
      this.killBot(victim, killer.name);
      return true;
    }
    const prevHp = victim.hp;
    victim.hp -= damage;
    if (victim.hp <= 0) {
      this.knockBot(victim, killer.name);
      return false;
    }
    this.flashBotHurt(victim, damage, prevHp);
    return false;
  }

  private findNearestTarget(bot: Bot, playerPos: THREE.Vector3): CombatTarget | null {
    const profile = BOT_LOADOUT_PROFILES[bot.loadout];
    const bpx = bot.root.position.x;
    const bpz = bot.root.position.z;
    let best: CombatTarget | null = null;

    const pdx = playerPos.x - bpx;
    const pdz = playerPos.z - bpz;
    const pDist = Math.hypot(pdx, pdz);
    if (pDist < profile.sightRange) {
      best = { kind: 'player', pos: playerPos, dist: pDist };
    }

    for (const other of this.bots) {
      if (other.id === bot.id || other.state === 'dead' || other.squadId === bot.squadId) continue;
      const dx = other.root.position.x - bpx;
      const dz = other.root.position.z - bpz;
      const dist = Math.hypot(dx, dz);
      const targetBias = other.state === 'knocked' ? 0.85 : 1;
      if (dist < profile.sightRange * targetBias && (!best || dist < best.dist)) {
        best = {
          kind: 'bot',
          pos: other.root.position,
          dist,
          bot: other,
        };
      }
    }

    return best;
  }

  /** Centralized trigger for bot panic wall + ramp build. */
  public triggerBotPanicBuild(liveBotIndex: number, playerPos: THREE.Vector3): void {
    const liveBots = this.getLivingBots();
    if (liveBotIndex < 0 || liveBotIndex >= liveBots.length) return;
    const lb = liveBots[liveBotIndex]!;
    const bot = this.bots.find((b) => b.id === lb.id);
    if (!bot || bot.state === 'dead' || bot.state === 'knocked') return;

    // Face the player/attacker
    const dx = playerPos.x - bot.root.position.x;
    const dz = playerPos.z - bot.root.position.z;
    const angle = Math.atan2(dx, dz);
    bot.yaw = angle;
    bot.root.rotation.y = angle;

    this.requestBotBuildWindow(bot, playerPos, true, true);
  }

  /** Called from player camera-space; returns any damage delivered to player. */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    playerCanBeHit: boolean,
    storm?: FpsStormContext,
    loot?: FpsLoot,
    buildPlacer?: FpsBuildPlacer,
    playerSniperGlint = false,
  ): BotUpdateResult {
    let damageToPlayer = 0;
    const tracerEvents: BotShotEvent[] = [];
    const footstepEvents: BotFootstepEvent[] = [];
    const buildCoverEvents: string[] = [];
    const rebootEvents: BotRebootEvent[] = [];

    this.botBuilds.update(dt);
    this.playerSniperGlintReactCd = Math.max(0, this.playerSniperGlintReactCd - dt);

    this.updateRadioChatter(dt);

    const liveBotsSnapshot = this.getLivingBots();
    const liveIndexById = new Map<number, number>();
    for (let li = 0; li < liveBotsSnapshot.length; li++) {
      liveIndexById.set(liveBotsSnapshot[li]!.id, li);
    }

    for (const [squadId, cd] of this.squadFireCooldowns) {
      const next = cd - dt;
      if (next <= 0) this.squadFireCooldowns.delete(squadId);
      else this.squadFireCooldowns.set(squadId, next);
    }

    if (this.lastPlayerPos) {
      const playerMoved = Math.hypot(playerPos.x - this.lastPlayerPos.x, playerPos.z - this.lastPlayerPos.z);
      this.playerStationaryTimer = playerMoved < 0.08 ? this.playerStationaryTimer + dt : 0;
      this.lastPlayerPos.copy(playerPos);
    } else {
      this.lastPlayerPos = playerPos.clone();
      this.playerStationaryTimer = 0;
    }
    const playerStationary = this.playerStationaryTimer > 1.15;

    for (const bot of this.bots) {
      if (bot.state === 'dead') {
        this.updateRagdoll(bot, dt);
        continue;
      }

      if (bot.state === 'knocked') {
        bot.knockedBleedout -= dt;
        const rebootEv = this.updateKnockedBot(bot, dt, camera);
        if (rebootEv) rebootEvents.push(rebootEv);
        if (bot.state === 'knocked' && bot.knockedBleedout <= 0) {
          this.killBot(bot);
        }
        continue;
      }

      const bpx = bot.root.position.x;
      const bpz = bot.root.position.z;

      // Decrement living bot timers
      if (bot.editTimer && bot.editTimer > 0) {
        bot.editTimer = Math.max(0, bot.editTimer - dt);
      }
      if (bot.swingTimer && bot.swingTimer > 0) {
        bot.swingTimer = Math.max(0, bot.swingTimer - dt);
      }
      if (bot.suppressedFireTimer > 0) {
        bot.suppressedFireTimer = Math.max(0, bot.suppressedFireTimer - dt);
      }
      if (bot.calloutCd > 0) {
        bot.calloutCd = Math.max(0, bot.calloutCd - dt);
      }
      if (bot.intentCalloutCd > 0) {
        bot.intentCalloutCd = Math.max(0, bot.intentCalloutCd - dt);
      }
      if (bot.sniperGlintCd > 0) {
        bot.sniperGlintCd = Math.max(0, bot.sniperGlintCd - dt);
      }
      if (bot.pushTelegraphCd > 0) {
        bot.pushTelegraphCd = Math.max(0, bot.pushTelegraphCd - dt);
      }
      if (bot.pushTelegraphT > 0) {
        bot.pushTelegraphT = Math.max(0, bot.pushTelegraphT - dt);
      }
      if (bot.buildTelegraphCd > 0) {
        bot.buildTelegraphCd = Math.max(0, bot.buildTelegraphCd - dt);
      }
      if (bot.pendingBuildT > 0) {
        bot.pendingBuildT = Math.max(0, bot.pendingBuildT - dt);
      }
      if (bot.emoteT > 0) {
        this.applyBotEmotePose(bot, dt);
        continue;
      }
      this.updateSniperGlint(bot, dt);
      this.resolvePendingBotBuild(bot, buildCoverEvents);

      // Gravity vertical update for jumping
      if (bot.jumpVy === undefined) {
        bot.jumpVy = 0;
      }
      if (bot.jumpVy !== 0 || bot.body.position.y > 0) {
        bot.jumpVy -= 15 * dt; // Gravity
        bot.body.position.y += bot.jumpVy * dt;
        if (bot.body.position.y <= 0) {
          bot.body.position.y = 0;
          bot.jumpVy = 0;
        }
      }

      const inStorm = storm?.isOutside(bpx, bpz) ?? false;
      const combatTarget = inStorm ? null : this.findNearestTarget(bot, playerPos);
      const distToTarget = combatTarget?.dist ?? Infinity;
      const profile = BOT_LOADOUT_PROFILES[bot.loadout];
      const roleProfile = BOT_ROLE_PROFILES[bot.role];
      const killLeaderBoost = this.isKillLeader(bot);
      const liveIdx = liveIndexById.get(bot.id);
      const moveOverride = liveIdx != null ? this.botMoveOverrides.get(liveIdx) : undefined;
      const shootRange = bot.role === 'sniper'
        ? Math.max(profile.shootRange, roleProfile.preferredDistance + 8)
        : profile.shootRange;
      const preferredDistance = Math.min(shootRange * 0.96, roleProfile.preferredDistance * (this.endgameMode ? 1.14 : 1));

      const knockedSquadmate = bot.crawlToSquadmate
        ? this.bots.find((b) => b.squadId === bot.squadId && b.state === 'knocked')
        : null;
      if (knockedSquadmate == null) bot.crawlToSquadmate = false;

      const chestTarget =
        !inStorm && !combatTarget && !knockedSquadmate && loot
          ? this.findNearestUnopenedChest(bot, loot)
          : null;

      let supplyTarget: { x: number; z: number; dist: number } | null = null;
      if (!inStorm && !combatTarget && !knockedSquadmate && this.supplyDropTarget) {
        const sdx = this.supplyDropTarget.x - bpx;
        const sdz = this.supplyDropTarget.z - bpz;
        const sDist = Math.hypot(sdx, sdz);
        if (sDist < 72) {
          supplyTarget = { x: this.supplyDropTarget.x, z: this.supplyDropTarget.z, dist: sDist };
        }
      }

      let llamaTarget: { x: number; z: number; dist: number } | null = null;
      if (!inStorm && !combatTarget && !knockedSquadmate && this.llamaTarget) {
        const ldx = this.llamaTarget.x - bpx;
        const ldz = this.llamaTarget.z - bpz;
        const lDist = Math.hypot(ldx, ldz);
        if (lDist < 64) {
          llamaTarget = { x: this.llamaTarget.x, z: this.llamaTarget.z, dist: lDist };
        }
      }

      // State transitions (combat suppressed while fleeing storm)
      const prevState = bot.state;
      if (combatTarget) {
        if (distToTarget < shootRange) {
          bot.state = 'shoot';
        } else {
          bot.state = 'chase';
        }
      } else if (!inStorm && bot.state !== 'patrol') {
        bot.state = 'patrol';
      }
      if (combatTarget?.kind === 'player' && prevState !== bot.state && (bot.state === 'chase' || bot.state === 'shoot')) {
        this.maybeBotRoleCallout(bot, bot.role === 'flanker' ? 'flank' : 'push');
        this.emitSquadTacticalCallout(bot.name, 'push');
      }
      if (combatTarget?.kind === 'player' && (bot.state === 'chase' || bot.state === 'shoot')) {
        this.telegraphBotIntent(bot, distToTarget);
      }
      if (combatTarget?.kind === 'player' && (bot.state === 'chase' || bot.state === 'shoot')) {
        this.telegraphPushWindow(bot, distToTarget);
      }
      if (
        playerSniperGlint &&
        combatTarget?.kind === 'player' &&
        distToTarget <= PLAYER_SNIPER_GLINT_RANGE &&
        distToTarget >= 12
      ) {
        bot.seekCoverTimer = Math.max(bot.seekCoverTimer, 1.8);
        bot.suppressedFireTimer = Math.max(bot.suppressedFireTimer, 0.55);
        bot.shootCd = Math.max(bot.shootCd, 0.35);
        if (bot.state === 'chase') {
          bot.state = 'shoot';
        }
        if (this.playerSniperGlintReactCd <= 0 && Math.random() < 0.42) {
          this.playerSniperGlintReactCd = PLAYER_SNIPER_GLINT_REACT_CD;
          this.emitBotCallout(bot, 'Scope glint — take cover!', BOT_INTENT_CALLOUT_COOLDOWN);
          this.emitSquadTacticalCallout(bot.name, 'retreat');
          this.requestBotBuildWindow(bot, playerPos, true, false);
        }
      }

      // Choose movement target — storm flee overrides combat
      let tx = bot.patrolTarget.x;
      let tz = bot.patrolTarget.z;
      let desiredSpeed = PATROL_SPEED;
      let faceTx = tx;
      let faceTz = tz;

      if (inStorm && storm) {
        // Phase 33X-11: flee urgency scales with distance outside safe zone
        tx = storm.centerX;
        tz = storm.centerZ;
        faceTx = tx;
        faceTz = tz;
        const distOut = Math.max(
          0,
          Math.hypot(bpx - storm.centerX, bpz - storm.centerZ) - storm.safeRadius,
        );
        const urgency = Math.min(1, distOut / 40);
        desiredSpeed = CHASE_SPEED * (1.15 + urgency * 0.45);
        bot.state = 'chase';
      } else if (combatTarget && (bot.state === 'chase' || bot.state === 'shoot')) {
        if (bot.state === 'shoot' && distToTarget < preferredDistance * 0.75) {
          const awayLen = Math.max(0.001, distToTarget);
          tx = bpx - ((combatTarget.pos.x - bpx) / awayLen) * preferredDistance;
          tz = bpz - ((combatTarget.pos.z - bpz) / awayLen) * preferredDistance;
        } else {
          tx = combatTarget.pos.x;
          tz = combatTarget.pos.z;
        }
        faceTx = combatTarget.pos.x;
        faceTz = combatTarget.pos.z;
        if (bot.state === 'shoot') {
          desiredSpeed = distToTarget > preferredDistance * 1.15 ? PATROL_SPEED * 0.82 : PATROL_SPEED * 0.62;
        } else {
          const endgamePushMult = this.endgameMode && combatTarget.kind === 'player' ? 0.88 : 1;
          const leaderPushMult = killLeaderBoost && combatTarget.kind === 'player' ? 1.14 : 1;
          desiredSpeed = CHASE_SPEED * roleProfile.chaseSpeedMult * endgamePushMult * leaderPushMult;
          if (combatTarget.kind === 'player' && bot.pushTelegraphT > 0) {
            desiredSpeed *= 0.32;
          }
        }
      } else if (knockedSquadmate) {
        tx = knockedSquadmate.root.position.x;
        tz = knockedSquadmate.root.position.z;
        faceTx = tx;
        faceTz = tz;
        desiredSpeed = SQUADMATE_CRAWL_SPEED;
        bot.state = 'patrol';
      } else if (
        llamaTarget &&
        (!supplyTarget || llamaTarget.dist < supplyTarget.dist * 0.95) &&
        (!chestTarget || llamaTarget.dist < chestTarget.dist * 0.9)
      ) {
        tx = llamaTarget.x;
        tz = llamaTarget.z;
        faceTx = tx;
        faceTz = tz;
        desiredSpeed = CHASE_SPEED;
        bot.state = 'chase';
      } else if (
        supplyTarget &&
        (!chestTarget || supplyTarget.dist < chestTarget.dist * 0.92)
      ) {
        tx = supplyTarget.x;
        tz = supplyTarget.z;
        faceTx = tx;
        faceTz = tz;
        desiredSpeed = CHASE_SPEED * 0.95;
        bot.state = 'chase';
      } else if (moveOverride && !combatTarget) {
        tx = moveOverride.x;
        tz = moveOverride.z;
        faceTx = tx;
        faceTz = tz;
        desiredSpeed = moveOverride.storm ? CHASE_SPEED * 1.05 : CHASE_SPEED * 0.92;
        bot.state = 'chase';
      } else if (chestTarget) {
        tx = chestTarget.x;
        tz = chestTarget.z;
        faceTx = tx;
        faceTz = tz;
        desiredSpeed = PATROL_SPEED * 1.15;
        if (chestTarget.dist < CHEST_LOOT_RANGE) {
          this.tryBotOpenChest(bot, loot!, chestTarget.id);
        }
      }

      const tdx = tx - bpx;
      const tdz = tz - bpz;
      const tDist = Math.hypot(tdx, tdz);

      if (bot.state === 'patrol' && !inStorm && tDist < 0.9) {
        bot.patrolWait += dt;
        if (bot.patrolWait > 1.4) {
          bot.patrolWait = 0;
          bot.patrolTarget = this.pickPatrolPoint(bpx, bpz);
        }
      } else {
        bot.patrolWait = 0;
      }

      // Move toward target (stop in shoot if close enough)
      const prevX = bot.root.position.x;
      const prevZ = bot.root.position.z;
      const holdingPreferredDistance =
        bot.state === 'shoot' &&
        distToTarget >= preferredDistance * 0.75 &&
        distToTarget <= preferredDistance * 1.15;
      const shouldMove = !holdingPreferredDistance;
      let isBlockedByWall = false;
      let blockedBuildObject: THREE.Object3D | null = null;
      let nx = 0;
      let nz = 0;

      if (shouldMove && tDist > 0.4) {
        nx = tdx / tDist;
        nz = tdz / tDist;

        // Perform raycast check for builds (both player builds and bot builds)
        let obstacleMeshes: THREE.Object3D[] = [];
        if (buildPlacer) {
          obstacleMeshes = obstacleMeshes.concat(buildPlacer.getPlacedBuilds().map((b) => b.mesh));
        }
        obstacleMeshes = obstacleMeshes.concat(this.botBuilds.getMeshesForRaycast());

        if (obstacleMeshes.length > 0) {
          const blockRaycaster = new THREE.Raycaster();
          const rayOrigin = new THREE.Vector3(bpx, bot.root.position.y + 0.6, bpz);
          const rayDir = new THREE.Vector3(nx, 0, nz).normalize();
          blockRaycaster.set(rayOrigin, rayDir);
          blockRaycaster.far = 1.6;

          const hits = blockRaycaster.intersectObjects(obstacleMeshes, true);
          if (hits.length > 0) {
            isBlockedByWall = true;
            blockedBuildObject = hits[0].object;
          }
        }

        // Handle blocked path vs free path
        let canMove = true;
        if (isBlockedByWall && blockedBuildObject) {
          const isBotBuild = blockedBuildObject.userData.isBotBuild === true;
          const wallBotId = isBotBuild ? this.botBuilds.getWallBotId(blockedBuildObject) : -1;
          const isOwnBuild = isBotBuild && wallBotId === bot.id;

          if (isOwnBuild) {
            // Edit own build!
            if (!bot.editTimer || bot.editTimer === 0) {
              bot.editTimer = 0.45; // 450ms edit delay
              if (blockedBuildObject instanceof THREE.Mesh) {
                const mat = blockedBuildObject.material as THREE.MeshToonMaterial;
                if (mat && mat.emissive) {
                  mat.emissive.setHex(0xffdd44); // yellow edit highlight
                  mat.emissiveIntensity = 0.85;
                }
              }
              if (bot.jumpVy === 0) {
                bot.jumpVy = 4.8; // jump over!
              }
            }
            if (bot.editTimer > 0) {
              canMove = false; // pause to edit
              bot.rightArm.rotation.x = -1.15; // raise arm
            }
          } else {
            // Break opponent/player build!
            canMove = false;
            // Face the wall to break it
            faceTx = blockedBuildObject.position.x;
            faceTz = blockedBuildObject.position.z;

            if (!bot.swingTimer || bot.swingTimer === 0) {
              bot.swingTimer = 0.5; // swing every 500ms
              bot.rightArm.rotation.x = -1.25; // wind up swing

              if (this.onBuildDamage) {
                this.onBuildDamage(blockedBuildObject, 25);
              }
              if (Math.random() < 0.35 && bot.jumpVy === 0) {
                bot.jumpVy = 5.2; // leap while swinging!
              }
            }
          }
        }

        if (canMove) {
          bot.root.position.x += nx * desiredSpeed * dt;
          bot.root.position.z += nz * desiredSpeed * dt;
          bot.walkPhase += dt * desiredSpeed * 2.3;
        }
      }

      // Occasional strafe while shooting
      if (bot.state === 'shoot' && combatTarget && tDist > 0.05) {
        bot.strafeTimer -= dt;
        if (bot.strafeTimer <= 0) {
          bot.strafeTimer = 0.8 + Math.random() * 0.7;
          bot.strafeDir = Math.random() < 0.5 ? 1 : -1;
        }
        if (bot.strafeDir !== 0) {
          const perpX = (-tdz / tDist) * bot.strafeDir;
          const perpZ = (tdx / tDist) * bot.strafeDir;
          const combatFactor = Math.max(0, Math.min(1, 1 - distToTarget / 45));
          const endgameStrafeMult = this.endgameMode ? 1.08 : 1;
          bot.root.position.x += perpX * roleProfile.strafeSpeed * endgameStrafeMult * combatFactor * dt;
          bot.root.position.z += perpZ * roleProfile.strafeSpeed * endgameStrafeMult * combatFactor * dt;
          if (bot.role === 'flanker' && combatTarget.kind === 'player') {
            this.maybeBotRoleCallout(bot, 'flank');
          }
        }
      } else {
        bot.strafeDir = 0;
      }

      // Cover-seeking: retreat from player briefly after taking damage
      if (bot.seekCoverTimer > 0) {
        bot.seekCoverTimer -= dt;
        const awayDx = bot.root.position.x - playerPos.x;
        const awayDz = bot.root.position.z - playerPos.z;
        const awayLen = Math.hypot(awayDx, awayDz);
        if (awayLen > 0.01) {
          bot.root.position.x += (awayDx / awayLen) * 5 * dt;
          bot.root.position.z += (awayDz / awayLen) * 5 * dt;
          this.maybeBotRoleCallout(bot, 'retreat');
          this.emitSquadTacticalCallout(bot.name, 'retreat');
        }
      }

      const moved = Math.hypot(bot.root.position.x - prevX, bot.root.position.z - prevZ);
      if (moved > 0.02) {
        bot.stepDistAcc += moved;
        if (bot.stepDistAcc >= BOT_STEP_DIST) {
          bot.stepDistAcc = 0;
          footstepEvents.push({ x: bot.root.position.x, z: bot.root.position.z });
        }
      } else {
        bot.stepDistAcc *= 0.85;
      }

      // Face combat target (or movement target while fleeing)
      const fdx = faceTx - bpx;
      const fdz = faceTz - bpz;
      const desiredYaw = Math.atan2(fdx, fdz);
      const dy = ((desiredYaw - bot.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      bot.yaw += dy * Math.min(1, 5 * dt);
      bot.body.rotation.y = bot.yaw;

      // Limb animation via FpsCharacterAnimator
      const moving = shouldMove && tDist > 0.4;
      const horizSpeed = Math.hypot(bot.velocity.x, bot.velocity.z);
      {
        const botAnimState: CharAnimState = {
          velocityXZ: moving ? Math.max(1.5, horizSpeed) : 0,
          isSprinting: bot.state === 'chase' && horizSpeed > 3,
          isAirborne: bot.jumpVy > 0.5,
          isCrouching: bot.seekCoverTimer > 0,
          isShooting: bot.shotFiredThisFrame,
          isDead: false,
          deathTime: bot.deathT,
        };
        bot.animator.update(dt, botAnimState);
        bot.shotFiredThisFrame = false;
      }

      // Pickaxe / edit arm override (after animator to take precedence)
      if (bot.swingTimer && bot.swingTimer > 0) {
        const t = bot.swingTimer / 0.5;
        bot.rightArm.rotation.x = -1.35 * Math.sin(t * Math.PI);
      } else if (bot.editTimer && bot.editTimer > 0) {
        const t = bot.editTimer / 0.45;
        bot.rightArm.rotation.x = -1.15 * Math.sin(t * Math.PI);
      }

      // Shooting (player or other bots)
      bot.shootCd -= dt;
      const canShootPlayer = combatTarget?.kind === 'player' && playerCanBeHit;
      const canShootBot = combatTarget?.kind === 'bot' && combatTarget.bot;
      const squadCanFire = (this.squadFireCooldowns.get(bot.squadId) ?? 0) <= 0;
      const needsSniperGlint = canShootPlayer && (bot.role === 'sniper' || bot.loadout === 'sniper');
      if (!needsSniperGlint) {
        bot.sniperShotPrimed = false;
      }
      if (needsSniperGlint && !bot.sniperShotPrimed && bot.state === 'shoot' && bot.shootCd <= BOT_SNIPER_GLINT_WINDOW) {
        this.telegraphSniperAim(bot);
      }
      if (needsSniperGlint && bot.sniperShotPrimed && bot.sniperGlintT > 0) {
        bot.shootCd = Math.max(bot.shootCd, bot.sniperGlintT);
      }
      if (bot.state === 'shoot' && bot.shootCd <= 0 && squadCanFire && (canShootPlayer || canShootBot)) {
        const suppressedMult = bot.suppressedFireTimer > 0 ? 1.65 : 1;
        const leaderAccMult = killLeaderBoost ? 1.08 : 1;
        bot.shootCd =
          (profile.shootCd * roleProfile.fireCdMult * suppressedMult) / (this.pressureMultiplier * leaderAccMult) +
          this.reactionTimeSec * 0.35 +
          bot.fireJitter +
          Math.random() * 0.25;
        this.squadFireCooldowns.set(bot.squadId, SQUAD_FIRE_STAGGER + bot.fireJitter * 0.35);
        const origin = new THREE.Vector3(
          bpx + Math.sin(bot.yaw) * 0.4,
          bot.root.position.y + 1.4,
          bpz + Math.cos(bot.yaw) * 0.4,
        );
        const aimPos = combatTarget!.pos.clone();
        aimPos.y += 1.35;
        const shotDist = origin.distanceTo(aimPos);
        const inacc =
          (Math.min(0.12, shotDist * 0.0028 + 0.02) * profile.spreadMult) /
          (roleProfile.accuracyMult * this.pressureMultiplier * leaderAccMult);
        const tgt = aimPos.clone();
        tgt.x += (Math.random() - 0.5) * inacc * shotDist;
        tgt.y += (Math.random() - 0.5) * inacc * shotDist * 0.35;
        tgt.z += (Math.random() - 0.5) * inacc * shotDist;
        const dir = tgt.clone().sub(origin).normalize();
        const willHit =
          Math.random() <
          this.botHitChance(shotDist) * roleProfile.accuracyMult * leaderAccMult * (bot.strafeDir !== 0 ? 0.82 : 1.0);
        const dmg = willHit ? profile.damage : 0;
        if (willHit) {
          if (canShootPlayer) {
            damageToPlayer += dmg;
          } else if (canShootBot && combatTarget!.bot) {
            this.damageBotFromBot(combatTarget.bot, dmg, bot);
          }
        }
        tracerEvents.push({
          origin,
          dir,
          damage: dmg,
          botPos: bot.root.position.clone(),
          targetPos: aimPos,
          botName: bot.name,
          botId: bot.id,
          loadout: bot.loadout,
        });
        this.flashBotMuzzle(bot);
        bot.sniperShotPrimed = false;
        bot.shotFiredThisFrame = true;
        if (
          canShootPlayer &&
          distToTarget <= BOT_COMBAT_BUILD_RANGE &&
          Math.random() < BOT_SHOOT_BUILD_CHANCE * (bot.role === 'builder' ? 1.8 : 1) * (this.endgameMode ? 1.45 : 1)
        ) {
          this.requestBotBuildWindow(bot, playerPos, false);
        }
      }

      if (
        bot.role === 'builder' &&
        combatTarget?.kind === 'player' &&
        distToTarget <= BOT_COMBAT_BUILD_RANGE &&
        (playerStationary || playerPos.y > bot.root.position.y + 1.35) &&
        Math.random() < BOT_BUILDER_PRESSURE_BUILD_CHANCE_PER_SEC * (this.endgameMode ? 1.35 : 1) * dt
      ) {
        this.requestBotBuildWindow(bot, playerPos, true);
      }

      // Update HUD anchors
      this.updateBotRimLight(bot, playerPos);
      this.updateBotHud(bot, camera);
    }

    // Keep bot list clean: prune long-dead ones after ragdoll
    for (let i = this.bots.length - 1; i >= 0; i--) {
      const b = this.bots[i]!;
      if (b.state === 'dead' && b.deathT > RAGDOLL_LIFE) {
        this.disposeBot(b);
        this.bots.splice(i, 1);
      }
    }

    return { damageToPlayer, tracerEvents, footstepEvents, buildCoverEvents, rebootEvents };
  }

  private flashBotMuzzle(bot: Bot): void {
    const flash = new THREE.PointLight(0xffcc66, 4.5, 6);
    bot.gun.add(flash);
    flash.position.set(0, 0, 0.55);
    this.scheduleTimeout(() => {
      bot.gun.remove(flash);
      flash.dispose();
    }, 60);
  }

  private updateKnockedBot(
    bot: Bot,
    dt: number,
    camera: THREE.PerspectiveCamera,
  ): BotRebootEvent | null {
    bot.body.rotation.z = Math.sin(performance.now() * 0.0018 + bot.id) * 0.03;

    if (!bot.rebootUsed) {
      const bpx = bot.root.position.x;
      const bpz = bot.root.position.z;
      const vanDx = REBOOT_VAN_X - bpx;
      const vanDz = REBOOT_VAN_Z - bpz;
      const vanDist = Math.hypot(vanDx, vanDz);

      if (vanDist <= REBOOT_VAN_RADIUS) {
        bot.vanRebootTimer += dt;
        if (vanDist > 0.35) {
          const crawl = SQUADMATE_CRAWL_SPEED * dt;
          bot.root.position.x += (vanDx / vanDist) * crawl;
          bot.root.position.z += (vanDz / vanDist) * crawl;
          const desiredYaw = Math.atan2(vanDx, vanDz);
          const dy = ((desiredYaw - bot.yaw + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          bot.yaw += dy * Math.min(1, 3 * dt);
          bot.body.rotation.y = bot.yaw;
        }
        if (bot.vanRebootTimer >= REBOOT_VAN_HOLD) {
          this.rebootBotFromVan(bot);
          this.updateBotHud(bot, camera);
          return { name: bot.name };
        }
      } else {
        bot.vanRebootTimer = 0;
      }
    }

    this.updateBotHud(bot, camera);
    return null;
  }

  private updateRagdoll(bot: Bot, dt: number): void {
    bot.deathT += dt;
    bot.fallVy -= 5 * dt;
    bot.body.position.y += bot.fallVy * dt;
    bot.body.rotation.z += dt * 1.8;
    bot.body.rotation.x = Math.min(Math.PI / 2, bot.body.rotation.x + dt * 1.6);
    if (bot.body.position.y < -1.4) {
      bot.body.position.y = -1.4;
      bot.fallVy = 0;
    }
    const dissolveT = Math.min(1, bot.deathT / RAGDOLL_LIFE);
    const scale = Math.max(0.04, 1 - dissolveT * 0.92);
    const opacity = Math.max(0, 1 - dissolveT * 1.08);
    bot.body.scale.setScalar(scale);
    bot.root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (!('opacity' in mat)) continue;
        mat.transparent = true;
        mat.opacity = opacity;
      }
    });
  }

  private updateBotRimLight(bot: Bot, playerPos: THREE.Vector3): void {
    if (bot.state === 'dead') return;
    const dx = bot.root.position.x - playerPos.x;
    const dz = bot.root.position.z - playerPos.z;
    const dist = Math.hypot(dx, dz);
    const near = Math.max(0, 1 - dist / BOT_RIM_LIGHT_RANGE);
    const mat = bot.torso.material as THREE.MeshToonMaterial;
    mat.emissiveIntensity = bot.baseTorsoEmissiveIntensity + near * 0.38;
    if (near > 0.02) {
      mat.emissive.copy(bot.baseTorsoEmissive).lerp(new THREE.Color(0xaaccff), near * 0.28);
    } else {
      mat.emissive.copy(bot.baseTorsoEmissive);
    }
  }

  private updateBotHud(bot: Bot, camera: THREE.PerspectiveCamera): void {
    const worldPos = new THREE.Vector3(
      bot.root.position.x,
      bot.root.position.y + 2.5,
      bot.root.position.z,
    );
    const projected = worldPos.clone().project(camera);
    const inFront = projected.z < 1;
    if (!inFront) {
      bot.hpBar.style.display = 'none';
      bot.nameTag.style.display = 'none';
      return;
    }
    const camDist = worldPos.distanceTo(camera.position);
    const fadeT = Math.min(
      1,
      Math.max(0, (camDist - BOT_NAMEPLATE_FADE_START) / (BOT_NAMEPLATE_FADE_END - BOT_NAMEPLATE_FADE_START)),
    );
    const nameOpacity = 1 - fadeT * 0.78;
    const nameScale = 1 - fadeT * 0.24;
    const x = (projected.x * 0.5 + 0.5) * window.innerWidth;
    const y = (1 - (projected.y * 0.5 + 0.5)) * window.innerHeight;
    bot.hpBar.style.left = `${x}px`;
    bot.hpBar.style.top = `${y}px`;
    bot.nameTag.style.left = `${x}px`;
    bot.nameTag.style.top = `${y - 8}px`;
    bot.hpBar.style.display = 'block';
    bot.nameTag.style.display = 'block';
    // Phase 30X-9: threat tint when bot is chasing/shooting the player
    const threatening = bot.state === 'shoot' || bot.state === 'chase';
    bot.nameTag.style.color = threatening ? '#ff6b6b' : '#e8f0ff';
    bot.nameTag.style.borderColor = threatening
      ? 'rgba(255,80,80,0.65)'
      : 'rgba(255,255,255,0.18)';
    bot.nameTag.style.opacity = String(Math.max(0.22, nameOpacity));
    bot.nameTag.style.transform = `translate(-50%,-100%) scale(${nameScale.toFixed(3)})`;
    bot.hpBar.style.opacity = String(Math.max(0.35, 1 - fadeT * 0.55));
    if (bot.state === 'knocked') {
      bot.hpFill.style.width = `${Math.max(0, (bot.knockedBleedout / KNOCK_BLEEDOUT) * 100)}%`;
      bot.hpFill.style.background = 'linear-gradient(90deg,#ff9a3c,#ffb86a)';
      bot.nameTag.style.color = '#ff9a3c';
      return;
    }
    bot.hpFill.style.width = `${Math.max(0, (bot.hp / MAX_HP) * 100)}%`;
    if (bot.hp < 35) {
      bot.hpFill.style.background = 'linear-gradient(90deg,#ff5252,#ffa040)';
    } else if (bot.hp < 65) {
      bot.hpFill.style.background = 'linear-gradient(90deg,#ffd23f,#9bff6a)';
    }
  }

  private disposeBot(bot: Bot): void {
    bot.root.removeFromParent();
    this.disposeObjectTree(bot.root);
    bot.hpBar.remove();
    bot.nameTag.remove();
  }

  /** Spawn a fast visible tracer between two points. */
  spawnTracer(scene: THREE.Scene, origin: THREE.Vector3, end: THREE.Vector3, color = 0xffcc66): void {
    const dir = end.clone().sub(origin);
    const len = dir.length();
    if (len < 0.02) return;
    const geo = new THREE.CylinderGeometry(0.02, 0.02, len, 6);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const mid = origin.clone().add(end).multiplyScalar(0.5);
    mesh.position.copy(mid);
    mesh.lookAt(end);
    mesh.rotateX(Math.PI / 2);
    scene.add(mesh);
    const t0 = performance.now();
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      this.tracerCleanups.delete(cleanup);
      geo.dispose();
      mat.dispose();
      scene.remove(mesh);
    };
    this.tracerCleanups.add(cleanup);
    const fade = () => {
      if (this.disposed) {
        cleanup();
        return;
      }
      const t = (performance.now() - t0) / 140;
      mat.opacity = Math.max(0, 0.85 * (1 - t));
      if (t < 1) requestAnimationFrame(fade);
      else {
        cleanup();
      }
    };
    requestAnimationFrame(fade);
  }

  /** Positions for spike-trap overlap checks. */
  getTrapCheckEntities(): { key: string; x: number; y: number; z: number }[] {
    const out: { key: string; x: number; y: number; z: number }[] = [];
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      out.push({
        key: `bot-${bot.id}`,
        x: bot.root.position.x,
        y: bot.root.position.y,
        z: bot.root.position.z,
      });
    }
    return out;
  }

  damageBotByTrap(id: number, damage: number, from: THREE.Vector3): BotHitResult | null {
    const bot = this.bots.find((b) => b.id === id);
    if (!bot || bot.state === 'dead') return null;
    const pos = bot.root.position.clone();
    if (bot.state === 'knocked') {
      this.killBot(bot, 'Spike trap');
      return { killed: true, damage, name: bot.name, headshot: false, pos };
    }
    const prevHp = bot.hp;
    bot.hp -= damage;
    if (bot.hp <= 0) {
      this.knockBot(bot);
      return { killed: false, knocked: true, damage, name: bot.name, headshot: false, pos };
    }
    this.flashBotHurt(bot, damage, prevHp);
    return { killed: false, damage, name: bot.name, headshot: false, pos };
  }

  /** Living bot aim points for touch aim assist. */
  getAliveBotAimTargets(): { x: number; y: number; z: number }[] {
    const out: { x: number; y: number; z: number }[] = [];
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      out.push({
        x: bot.root.position.x,
        y: bot.root.position.y + 1.35,
        z: bot.root.position.z,
      });
    }
    return out;
  }

  aliveCount(): number {
    return this.bots.filter((b) => b.state !== 'dead').length;
  }

  /** Snapshot bot transforms for match replay recording. */
  getReplayBotEntities(): Array<{ id: number; pos: THREE.Vector3; rot: THREE.Quaternion }> {
    const out: Array<{ id: number; pos: THREE.Vector3; rot: THREE.Quaternion }> = [];
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      out.push({
        id: bot.id,
        pos: bot.root.position.clone(),
        rot: bot.root.quaternion.clone(),
      });
    }
    return out;
  }

  getLivingBots(): {
    id: number;
    name: string;
    role: BotRole;
    loadout: BotLoadout;
    hp: number;
    weaponRarity: WeaponRarity;
    x: number;
    y: number;
    z: number;
    squadId: number;
    squadColor: string;
    knocked: boolean;
  }[] {
    const out: {
      id: number;
      name: string;
      role: BotRole;
      loadout: BotLoadout;
      hp: number;
      weaponRarity: WeaponRarity;
      x: number;
      y: number;
      z: number;
      squadId: number;
      squadColor: string;
      knocked: boolean;
    }[] = [];
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      out.push({
        id: bot.id,
        name: bot.name,
        role: bot.role,
        loadout: bot.loadout,
        hp: bot.state === 'knocked' ? 0 : bot.hp,
        weaponRarity: bot.weaponRarity,
        x: bot.root.position.x,
        y: bot.root.position.y,
        z: bot.root.position.z,
        squadId: bot.squadId,
        squadColor: getBotSquadColor(bot.squadId),
        knocked: bot.state === 'knocked',
      });
    }
    return out;
  }

  /** Ally / teammate damage — skips knock, straight to elim on 0 hp. */
  applyExternalDamage(
    botId: number,
    damage: number,
    killerName: string,
    threatPos: THREE.Vector3,
  ): BotHitResult | null {
    const bot = this.bots.find((b) => b.id === botId);
    if (!bot || bot.state === 'dead') return null;
    const pos = bot.root.position.clone();
    if (bot.state === 'knocked') {
      this.killBot(bot, killerName);
      return { killed: true, damage, name: bot.name, headshot: false, pos };
    }
    const prevHp = bot.hp;
    bot.hp -= damage;
    if (bot.hp <= 0) {
      this.killBot(bot, killerName);
      return { killed: true, damage, name: bot.name, headshot: false, pos };
    }
    this.flashBotHurt(bot, damage, prevHp);
    return { killed: false, damage, name: bot.name, headshot: false, pos };
  }

  /** Sorted ids of bots still in the match (alive or knocked). */
  getAliveBotIds(): number[] {
    const ids: number[] = [];
    for (const bot of this.bots) {
      if (bot.state !== 'dead') ids.push(bot.id);
    }
    ids.sort((a, b) => a - b);
    return ids;
  }

  /** Nearest living bot for death spectate camera. */
  getNearestAliveBot(fromX: number, fromZ: number): {
    id: number;
    name: string;
    x: number;
    y: number;
    z: number;
    yaw: number;
  } | null {
    let best: {
      id: number;
      name: string;
      x: number;
      y: number;
      z: number;
      yaw: number;
    } | null = null;
    let bestD2 = Infinity;
    for (const bot of this.bots) {
      if (bot.state === 'dead') continue;
      const dx = bot.root.position.x - fromX;
      const dz = bot.root.position.z - fromZ;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = {
          id: bot.id,
          name: bot.name,
          x: bot.root.position.x,
          y: bot.root.position.y,
          z: bot.root.position.z,
          yaw: bot.yaw,
        };
      }
    }
    return best;
  }

  /** Live bot pose for spectate follow (null if dead or missing). */
  getAliveBotPose(id: number): {
    name: string;
    x: number;
    y: number;
    z: number;
    yaw: number;
  } | null {
    const bot = this.bots.find((b) => b.id === id);
    if (!bot || bot.state === 'dead') return null;
    return {
      name: bot.name,
      x: bot.root.position.x,
      y: bot.root.position.y,
      z: bot.root.position.z,
      yaw: bot.yaw,
    };
  }

  totalCount(): number {
    return this.bots.length;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const id of this.scheduledTimeouts) window.clearTimeout(id);
    this.scheduledTimeouts.clear();
    for (const cleanup of [...this.tracerCleanups]) cleanup();
    this.tracerCleanups.clear();
    for (const b of this.bots) this.disposeBot(b);
    this.bots.length = 0;
    this.botBuilds.dispose();
    this.grad.dispose();
    this.hudLayer.remove();
  }
}
